import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import i18next from 'i18next';
import type { ReactNode } from 'react';

import { NATIVE_EVENTS } from '@/constants/events';
import { HostBridge } from '@/lib/hostBridge';
import { showSessionExpiredToast } from '@/lib/sessionExpiredToast';

interface HttpError {
  statusCode?: number;
  response?: { status?: number };
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: unknown) => {
      const err = error as HttpError;
      if (err.statusCode === 401 || err.response?.status === 401) {
        void HostBridge.continue(
          { action: 'exit', reason: 'unauthorized' },
          {
            message: i18next.t(
              'common.sessionExpiredTab',
              'Your session has expired. Please try again.',
            ),
            nativeEventType: NATIVE_EVENTS.GAME_UNAUTHORIZED,
            showToast: showSessionExpiredToast,
          },
        );
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error: unknown) => {
      const err = error as HttpError;
      if (err.statusCode === 401 || err.response?.status === 401) {
        void HostBridge.continue(
          { action: 'exit', reason: 'unauthorized' },
          {
            message: i18next.t(
              'common.sessionExpiredTab',
              'Your session has expired. Please try again.',
            ),
            nativeEventType: NATIVE_EVENTS.GAME_UNAUTHORIZED,
            showToast: showSessionExpiredToast,
          },
        );
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

interface QueryProviderProps {
  children: ReactNode;
}

export function QueryProvider({ children }: Readonly<QueryProviderProps>) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { queryClient };
